/* istanbul ignore file */
import { computed } from 'vue';
import { useAvailability } from '@common-vue/media-card-large';
import { useType } from '@modules/types';
import { useColor } from '@modules/color';
import { useSize } from '@modules/size/composables';
import { useKillswitches, useProductFlags } from '@modules/featureControl';
import { useUpc } from '@modules/upc';
import { useProduct } from '@modules/product';
import useSelectedMemberId from '@modules/latchkey/composables/useSelectedMemberId';
import { isAnyUpcAvailable } from '../utils/utils';

function getPriceRange(priceToColors) {
  const firstPrice = priceToColors?.[0]?.price;
  const lastPriceValue = priceToColors?.[priceToColors?.length - 1];
  const lastPrice = lastPriceValue?.price;

  return `${lastPrice}-${firstPrice}`;
}

export default function useColorVariant() {
  const { killswitches: { disablePriceByColors } } = useKillswitches();
  const { isTypeDrivenThirdVariantFlag: isTypeDriven } = useProductFlags();
  const { productId, productData: product } = useProduct();
  const { selectedSizeId } = useSize();
  const {
    color: colorAvailability,
  } = useAvailability(productId);
  const { traitsMap } = useUpc();
  const isSizeDriven = !isTypeDriven;
  const { selectedTypeId } = useType();
  const selectedMemberId = useSelectedMemberId();

  const { colorProps } = useColor();

  const typeDrivenColorFiltering = () => {
    const type = product.traits.types.typeMap[selectedTypeId.value] || { colorToSizes: {} };

    // Get color IDs relevant to the selected type
    const colorIdsForType = Object.keys(type.colorToSizes).map((id) => parseInt(id, 10)) || [];

    return {
      ...colorProps?.value,
      colors: colorProps?.value.colors
        .filter((color) => colorIdsForType.includes(color.id)) // Filter colors based on type
        .map((color) => ({
          ...color,
          available: colorAvailability.value[color.id], // mark availablity
        })),
    };
  };

  // Filter colors based on selected size, then mark availability
  const sizeDrivenColorFiltering = () => {
    const size = product.traits.sizes.sizeMap[selectedSizeId.value ?? -1] || { colorToTypes: {} };

    // Get color IDs relevant to the selected size
    const colorIdsForSize = Object.keys(size.colorToTypes).map((id) => parseInt(id, 10)) || [];

    return {
      ...colorProps?.value,
      colors: colorProps?.value.colors
        .filter((color) => colorIdsForSize.includes(color.id)) // Filter colors based on size
        .map((color) => ({
          ...color,
          available: colorAvailability.value[color.id], // mark availablity
        })),
    };
  };

  const filteredColorProps = computed(() => (isTypeDriven ? typeDrivenColorFiltering() : sizeDrivenColorFiltering()));

  const availableColors = computed(() => {
    const { colors } = filteredColorProps.value;
    if (isSizeDriven) {
      return colors.filter((color) => isAnyUpcAvailable(traitsMap.value, { colorId: color.id, sizeId: selectedSizeId.value }));
    }
    return colors.filter((color) => isAnyUpcAvailable(traitsMap.value, { colorId: color.id, typeId: selectedTypeId.value }));
  });

  const priceToColors = computed(() => {
    const memberPriceToColors = product.relationships.memberProductMap[selectedMemberId.value]?.traits?.traitsMaps.priceToColors;
    const isNotPriceByColors = !memberPriceToColors || (memberPriceToColors.length === 1);

    return (disablePriceByColors || isNotPriceByColors) ? [
      { colorIds: Object.keys(product.traits.colors?.colorMap || {}).map((key) => Number(key)) },
    ] : memberPriceToColors;
  });

  const priceToColorsExp = computed(() => [
    {
      colorIds: priceToColors.value?.flatMap((priceTier) => priceTier.colorIds) ?? [],
      onSale: priceToColors.value?.every((priceTier) => priceTier.onSale) ?? false,
      priceRange: ((priceToColors.value?.length > 1) && getPriceRange(priceToColors.value)) ?? '',
    },
  ]);

  return {
    filteredColorProps,
    availableColors,
    priceToColors,
    priceToColorsExp,
  };
}
